import React from "react";
import { Handle, Position, type NodeProps } from "reactflow";
import { FiChevronDown, FiChevronRight, FiDatabase, FiEye, FiHardDrive } from "react-icons/fi";

import { SchemaTableName } from "./SchemaTableName";
import type { HubNodeData } from "./types";
import { isVirtualizedConstruct } from "../model/constructs";
import { isInternalRegisteredAsset } from "./internalAssetStyling";
import { NotGeneratedBadge, notGeneratedNodeClasses } from "./notGeneratedStyling";

export function HubNode({ data, selected }: NodeProps<HubNodeData>): JSX.Element {
  const { entity, onSelect, satelliteCount = 0, collapsed = false, onToggleCollapse } = data;
  const handleBaseClass =
    "!tw-h-3 !tw-w-3 !tw-border !tw-border-blue-200 !tw-bg-white tw-translate-x-0";
  const constructType = entity.record.datavault_construct_type ?? "";
  const isVirtualized = isVirtualizedConstruct(constructType);
  const isInternal = isInternalRegisteredAsset(entity.record.data_source_type);
  const StatusIcon = isInternal ? FiDatabase : isVirtualized ? FiEye : FiHardDrive;
  const statusLabel = isInternal ? "Internal" : isVirtualized ? "Virtualized" : "Persisted";
  const baseClasses =
    "tw-group tw-relative tw-min-w-[200px] tw-cursor-grab tw-rounded-3xl tw-border tw-bg-gradient-to-br tw-from-white tw-via-blue-50 tw-to-blue-100 tw-p-5 tw-text-left tw-shadow-lg tw-transition-transform tw-duration-150 hover:tw--translate-y-1";
  const selectionClasses = selected
    ? "tw-border-[4px] tw-border-blue-900 tw-ring-[5px] tw-ring-blue-500/70 tw-shadow-[0_0_20px_rgba(30,64,175,0.5)]"
    : "tw-border-blue-100 tw-ring-1 tw-ring-blue-200/60";
  return (
    <div
      onClick={(event) => {
        if (event.shiftKey || event.metaKey || event.ctrlKey) {
          return;
        }
        onSelect(entity.id);
      }}
      className={`${baseClasses} ${selectionClasses} ${notGeneratedNodeClasses(entity.record)}`}
    >
      <NotGeneratedBadge record={entity.record} />
      <Handle
        type="target"
        id="hub-target-top"
        position={Position.Top}
        className={handleBaseClass}
      />
      <Handle
        type="target"
        id="hub-target-bottom"
        position={Position.Bottom}
        className={handleBaseClass}
      />
      <Handle
        type="source"
        id="hub-source-left"
        position={Position.Left}
        className="!tw-h-3 !tw-w-3 !tw-border-2 !tw-border-white !tw-bg-blue-400"
      />
      <Handle
        type="source"
        id="hub-source-right"
        position={Position.Right}
        className="!tw-h-3 !tw-w-3 !tw-border-2 !tw-border-white !tw-bg-blue-400"
      />
      <div className="tw-absolute tw-right-4 tw-top-4 tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-white/60 tw-bg-white/90 tw-px-2 tw-py-1 tw-text-[10px] tw-font-semibold tw-text-slate-600">
        <StatusIcon className={`tw-h-3 tw-w-3 ${isInternal || isVirtualized ? "tw-text-[#03234a]" : ""}`} />
        <span className={isInternal || isVirtualized ? "tw-text-[#03234a]" : "tw-text-slate-500"}>
          {statusLabel}
        </span>
      </div>
      <div className="tw-flex tw-items-center tw-gap-3">
        <div className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-blue-500/90 tw-text-white tw-shadow-lg">
          <span className="tw-text-base tw-font-semibold">H</span>
        </div>
        <div>
          <p className="tw-text-sm tw-font-semibold tw-text-slate-800">Hub</p>
          <p className="tw-text-[11px] tw-uppercase tw-tracking-wide tw-text-blue-600">
            Business key anchor
          </p>
        </div>
      </div>
      <div className="tw-mt-3">
        <SchemaTableName
          schema={entity.record.target_schema}
          table={entity.record.target_table}
          className="tw-block tw-font-mono tw-text-[13px] tw-font-semibold tw-leading-5 tw-text-slate-900 tw-break-words"
        />
      </div>
      {satelliteCount > 0 && onToggleCollapse ? (
        <button
          type="button"
          // nodrag/nopan stop React Flow from treating this as a node drag; stopPropagation keeps
          // the click from selecting the hub.
          className="nodrag nopan tw-mt-3 tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-blue-200 tw-bg-white/80 tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-blue-700 hover:tw-bg-blue-50"
          onClick={(event) => {
            event.stopPropagation();
            onToggleCollapse(entity.id);
          }}
          title={collapsed ? "Show satellites" : "Hide satellites"}
        >
          {collapsed ? (
            <FiChevronRight className="tw-h-3 tw-w-3" />
          ) : (
            <FiChevronDown className="tw-h-3 tw-w-3" />
          )}
          <span>
            {satelliteCount} {satelliteCount === 1 ? "satellite" : "satellites"}
            {collapsed ? " (hidden)" : ""}
          </span>
        </button>
      ) : null}
    </div>
  );
}
